import React, { useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { SearchIcon } from 'lucide-react';
import { Badge } from '../../components/ui/Badge';
import { Button } from '../../components/ui/Button';
import { EmptyState } from '../../components/ui/EmptyState';
import { Field, Input } from '../../components/ui/Field';
import { useNetwork } from '../../contexts/NetworkContext';
import { CAMPUS_INTAKE } from '../../types/network';

/**
 * Campus discovery. Reads the configured campus register, so a campus cannot
 * appear publicly — or appear to be operating — unless its record says so.
 */
export function CampusDirectory() {
  /* useNetwork returns the value object itself, not a { network } wrapper. */
  const network = useNetwork();
  const [query, setQuery] = useState('');
  const published = useMemo(() => network.campuses.filter(campus => campus.published), [network.campuses]);
  const matches = useMemo(() => {
    const needle = query.trim().toLowerCase();
    if (!needle) return published;
    return published.filter(campus => campus.name.toLowerCase().includes(needle) || campus.city.toLowerCase().includes(needle) || campus.state.toLowerCase().includes(needle));
  }, [published, query]);
  return <div className="mx-auto max-w-7xl px-4 py-10 sm:px-6 lg:py-14" data-id="element-9540" data-mp="1jy54xrd1lakr">
      <h1 className="text-h1 font-semibold text-ink" data-id="element-9541" data-mp="1jwbzgfe60wec">Campuses on Campuzon</h1>
      <p className="mt-3 max-w-[68ch] text-body-lg text-ink-muted" data-id="element-9542" data-mp="15xm16wfm0qoe">
        Campuzon opens campus by campus. Each entry shows its real operating status — preparing,
        recruiting representatives, piloting, or fully active — so you know what to expect before you
        create an account.
      </p>

      <div className="mt-7 max-w-md" data-id="element-9543" data-mp="1102er98rd7ld">
        <Field label="Find your campus" htmlFor="campus-search" data-id="element-9544" data-mp="1113wtxo6yucl">
          <div className="relative" data-id="element-9545" data-mp="2rc88eej9ax2e">
            <SearchIcon aria-hidden className="pointer-events-none absolute left-4 top-1/2 h-5 w-5 -translate-y-1/2 text-ink-muted" data-id="element-9546" data-mp="2vdbmowlnol1i" />
            <Input id="campus-search" value={query} onChange={event => setQuery(event.target.value)} placeholder="University, city or state" className="pl-12" autoComplete="off" data-id="element-9547" data-mp="82rrc6zzj0nd" />
          </div>
        </Field>
      </div>

      {matches.length === 0 ? <div className="mt-8" data-id="element-9548" data-mp="qvlyierd9oh1">
          <EmptyState icon={<SearchIcon aria-hidden className="h-6 w-6" data-id="element-9550" data-mp="qx6um8tbxfh0" />} title="No campus matches that" description="Campuzon may not be on your campus yet. Create an account and we will tell you when it opens — or apply to be the representative who opens it." action={<Link to="/network" data-id="element-9551" data-mp="d24obkkklo3o">
                <Button variant="secondary" data-id="element-9552" data-mp="1ajl8hne3wrle">Open a campus</Button>
              </Link>} data-id="element-9549" data-mp="quu95lod4mz7" />
        </div> : <ul className="mt-8 grid gap-4 sm:grid-cols-2 lg:grid-cols-3" data-id="element-9553" data-mp="15r1x8x2w68jr">
          {matches.map(campus => {
        const intake = CAMPUS_INTAKE[campus.recruitmentStatus];
        return <li key={campus.id} className="flex flex-col rounded-card border border-line bg-surface p-5" data-id="element-9554" data-mp="1i3dymu0j18mw">
                <div className="flex items-start justify-between gap-3" data-id="element-9555" data-mp="1r42593nlc6pr">
                  <h2 className="text-h4 font-semibold text-ink" data-id="element-9556" data-mp="1r17mrho50vyz">{campus.name}</h2>
                  <Badge tone={campus.active ? 'ok' : 'warn'} plain={!campus.active} data-id="element-9557" data-mp="1w0j4ou08q7i5">
                    {campus.recruitmentStatus}
                  </Badge>
                </div>
                <p className="mt-1 text-body-sm text-ink-muted" data-id="element-9558" data-mp="25syjxjk5khzc">
                  {campus.city}, {campus.state}
                </p>
                <p className="mt-3 text-body-sm text-ink-muted" data-id="element-9559" data-mp="2atcm4kz2oxpg">{intake.note}</p>
                <p className="mt-2 text-caption text-ink-muted" data-id="element-9560" data-mp="2er1uxlxihwcw">
                  {campus.areas.length} mapped {campus.areas.length === 1 ? 'area' : 'areas'}
                </p>
                <div className="mt-auto flex flex-wrap gap-2 pt-4" data-id="element-9561" data-mp="263xwpaew5rgh">
                  <Link to={`/network/${campus.id}`} data-id="element-9562" data-mp="224p3q7rodxlz">
                    <Button size="sm" variant="secondary" data-id="element-9563" data-mp="221t4mg43d2vy">
                      Campus page
                    </Button>
                  </Link>
                  {campus.active && <Link to="/housing" data-id="element-9564" data-mp="2awzdxp2km338">
                      <Button size="sm" variant="ghost" data-id="element-9565" data-mp="1n6d4btcx4ar4">
                        Accommodation
                      </Button>
                    </Link>}
                </div>
              </li>;
      })}
        </ul>}

      <p className="mt-8 max-w-[68ch] border-t border-line pt-4 text-body-sm text-ink-muted" data-id="element-9566" data-mp="1x642q64ln9wh">
        Campus records are administrator configuration. Adding a campus needs no code change, and a
        campus that has not been published does not appear here at all.
      </p>
    </div>;
}